import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";

// Ordered pipeline for the tiny in-app markdown subset the education
// articles use. Runs on each paragraph string (after `\n\n` split) and
// returns HTML safe to hand to dangerouslySetInnerHTML.
//
// Escapes user-visible characters first so nothing from the article body
// can inject unintended tags — the source content is authored inside
// seed.py so the risk is low, but we still want defense in depth.
function renderInline(raw) {
    let s = raw
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    // [label](url) → anchor. Constrained to http(s) targets to avoid
    // javascript: schemes even if a malicious body somehow slipped through.
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
        (_, label, url) =>
            `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-accent-red underline underline-offset-2 hover:opacity-80">${label}</a>`);

    // PMID: 12345678 → live link to the PubMed abstract. Handles 6–9 digit
    // PMIDs (current PubMed IDs are 8, but the range is future-proofed).
    // Preserves the "PMID: " prefix inside the link so citation text still
    // reads naturally when rendered.
    s = s.replace(/PMID:\s*(\d{6,9})/g,
        (_, pmid) =>
            `<a href="https://pubmed.ncbi.nlm.nih.gov/${pmid}/" target="_blank" rel="noopener noreferrer" class="text-accent-red underline underline-offset-2 hover:opacity-80">PMID: ${pmid}</a>`);

    // Markdown bold and italic. Bold has to run before italic so a run of
    // `**text**` doesn't get eaten by the single-asterisk italic pass.
    s = s.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");

    return s;
}

export default function EducationArticle() {
    const { slug } = useParams();
    const [a, setA] = useState(null);
    useEffect(() => { api.get(`/education/${slug}`).then(r => setA(r.data)); }, [slug]);
    if (!a) return <div data-testid="article-loading">Loading…</div>;
    return (
        <article className="mx-auto max-w-3xl space-y-6" data-testid="article-page">
            <Link to="/education" className="label-eyebrow text-accent-red hover:underline" data-testid="article-back">← Back to library</Link>
            <div>
                <div className="label-eyebrow text-muted-foreground">{a.category} · {a.read_min} min read</div>
                <h1 className="mt-2 font-display text-4xl font-black uppercase leading-tight sm:text-5xl">{a.title}</h1>
            </div>
            <div className="space-y-4 text-base leading-relaxed text-foreground/90">
                {a.body.split(/\n\n+/).map((p, i) => (
                    <p key={i} data-testid={i === 0 ? "article-body" : undefined} dangerouslySetInnerHTML={{ __html: renderInline(p) }} />
                ))}
            </div>
        </article>
    );
}
